import { uuidModule } from "@/modules/uuid";
import {
  SpaceAccountNoteModelData,
  SpaceAccountNoteUpsertedSyncUpdateValue,
} from "@/store/recent-items/types";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import { IUpdateNoteReminderShillOperation } from "@/store/sync/operations/types";
import {
  OptimisticSyncUpdate,
  SyncModelData,
  SyncOperationKind,
  SyncUpdateValue,
} from "@/store/sync/types";

type ReminderShill = NonNullable<SpaceAccountNoteModelData["reminder_shill"]>;

type UpdateNoteReminderShillOptimisticData = {
  spaceAccountNote: SyncUpdateValue<SpaceAccountNoteModelData>;
};

/**
 * Persists reminder shill dismissal/click state and mirrors it onto the hydrated SpaceAccountNote.
 */
export class UpdateNoteReminderShillOperation extends BaseSyncOperation<IUpdateNoteReminderShillOperation> {
  private optimisticData: UpdateNoteReminderShillOptimisticData;

  constructor({
    optimisticData,
    ...params
  }: BaseSyncOperationParams<IUpdateNoteReminderShillOperation> & {
    optimisticData: UpdateNoteReminderShillOptimisticData;
  }) {
    super(params);
    this.optimisticData = optimisticData;
  }

  get operationKind(): SyncOperationKind {
    return "UPDATE_NOTE_REMINDER_SHILL";
  }

  async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const spaceAccountNote = this.optimisticData.spaceAccountNote;
    const currentReminderShill = spaceAccountNote.model_data.reminder_shill ?? undefined;
    const nextReminderShill = this.buildNextReminderShill(currentReminderShill);
    if (!nextReminderShill) return [];

    const spaceAccountNoteUpsertedValue: SpaceAccountNoteUpsertedSyncUpdateValue = {
      model_id: spaceAccountNote.model_id,
      model_kind: "SPACE_ACCOUNT_NOTE",
      model_version: spaceAccountNote.model_version ?? 0,
      model_data: {
        ...spaceAccountNote.model_data,
        reminder_shill: nextReminderShill,
      },
      model_scopes: spaceAccountNote.model_scopes,
    };

    const spaceAccountNoteUpserted: OptimisticSyncUpdate<SpaceAccountNoteModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: spaceAccountNoteUpsertedValue,
    };

    return [spaceAccountNoteUpserted];
  }

  async triggerRecompute() {
    const spaceAccountId =
      this.payload.space_account_id ?? this.store.spaceAccounts.myPersonalSpaceAccountId;
    const spaceAccountNoteId = uuidModule.resolveSpaceAccountNoteSyncModelUuid({
      spaceAccountId,
      noteId: this.payload.note_id,
    });
    await this.store.spaceAccountNotes.recompute(spaceAccountNoteId);
    await this.store.notes.recompute(this.payload.note_id);
  }

  private buildNextReminderShill(currentReminderShill: ReminderShill | undefined) {
    if (this.payload.action === "upsert") {
      return this.payload.reminder_shill ?? currentReminderShill;
    }
    if (!currentReminderShill) return undefined;

    if (this.payload.action === "dismiss") {
      return {
        ...currentReminderShill,
        is_dismissed: true,
      };
    }

    return {
      ...currentReminderShill,
      is_clicked: true,
    };
  }
}
